"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function JoinOurNextCelebration() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const workshops = [{
    day: "14",
    month: "MAR",
    year: "2025",
    title: "Caregiver Wellness Retreat",
    descriptor: "A restorative afternoon dedicated to mindfulness and peer support.",
    duration: "4 HOURS",
    seats: "8 SEATS",
    price: "FREE",
    status: "available",
    slug: "caregiver-wellness"
  }, {
    day: "22",
    month: "MAR",
    year: "2025",
    title: "Inclusive Art Exploration",
    descriptor: "Creative expression for neurodiverse youth and their families.",
    duration: "3 HOURS",
    seats: "10 SEATS",
    price: "FREE",
    status: "available",
    slug: "inclusive-art"
  }, {
    day: "05",
    month: "APR",
    year: "2025",
    title: "Advocacy Workshop",
    descriptor: "Navigating educational resources and legal rights for families.",
    duration: "5 HOURS",
    seats: "8 SEATS",
    price: "FREE",
    status: "waitlist",
    slug: "advocacy-training"
  }, {
    day: "19",
    month: "APR",
    year: "2025",
    title: "Sensory Garden Picnic",
    descriptor: "A community gathering designed for sensory-friendly enjoyment.",
    duration: "2 HOURS",
    seats: "12 SEATS",
    price: "FREE",
    status: "available",
    slug: "sensory-picnic"
  }, {
    day: "03",
    month: "MAY",
    year: "2025",
    title: "Bilingual Resource Fair",
    descriptor: "Connecting families with essential support services in our community.",
    duration: "6 HOURS",
    seats: "6 SEATS",
    price: "FREE",
    status: "sold-out",
    slug: "resource-fair"
  }, {
    day: "17",
    month: "MAY",
    year: "2025",
    title: "Family Connection Supper",
    descriptor: "A communal meal celebrating the strength of our diverse families.",
    duration: "4 HOURS",
    seats: "12 SEATS",
    price: "FREE",
    status: "available",
    slug: "family-supper"
  }, {
    day: "07",
    month: "JUN",
    year: "2025",
    title: "Music & Movement Therapy",
    descriptor: "Rhythmic engagement and joyful expression for all abilities.",
    duration: "4 HOURS",
    seats: "10 SEATS",
    price: "FREE",
    status: "available",
    slug: "music-therapy"
  }, {
    day: "21",
    month: "JUN",
    year: "2025",
    title: "Summer Community Celebration",
    descriptor: "An evening of connection, music, and neighborhood spirit.",
    duration: "3 HOURS",
    seats: "8 SEATS",
    price: "FREE",
    status: "waitlist",
    slug: "summer-celebration"
  }];
  return <section data-section-id="3951" id="join-our-next-celebration" className="dark bg-background text-foreground pt-28 md:pt-[140px] pb-24 md:pb-32 font-default">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {}
        <div ref={header.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 mb-20 md:mb-28 transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.2em] text-secondary mb-8 font-mono" style={{
            fontVariantCaps: "all-small-caps"
          }}>
              — Community Events / Spring 2025
            </p>
            <h2 className="font-default font-light text-5xl md:text-6xl lg:text-[72px] leading-[1.02] tracking-tight text-foreground">
              Gatherings,
              <br />
              for our
              <br />
              <span className="italic font-normal text-secondary">community.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-10 lg:pl-4">
            <p className="text-[17px] leading-relaxed text-muted-foreground font-default">
              Hope Torchlighters hosts inclusive events designed to support neurodiverse families. From respite care workshops to community celebrations, our programs are built to foster connection, provide resources, and ensure every family feels seen and supported.
            </p>
          </div>
        </div>

        {}
        <div className="border-t border-border/70">
          {workshops.map((w, i) => {
          const item = useRevealOnScroll<HTMLDivElement>({
            threshold: 0.1
          });
          const statusDot = w.status === "available" ? "bg-secondary" : w.status === "waitlist" ? "bg-transparent border border-muted-foreground/60" : "bg-muted-foreground/40";
          return <div data-index={i} ref={item.ref} key={w.slug} className={`group relative border-b border-border/70 transition-all duration-700 ease-out ${item.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDelay: `${i * 60}ms`
          }}>
                {}
                <div className="absolute inset-0 bg-accent/0 group-hover:bg-accent transition-colors duration-500 pointer-events-none" />

                {}
                <span className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-6 md:-translate-x-8 w-2 h-2 rounded-lg ${statusDot}`} aria-label={w.status} />

                <div className="relative grid grid-cols-12 gap-4 md:gap-6 items-center py-10 md:py-14">
                  {}
                  <div className="col-span-4 md:col-span-2">
                    <div className="font-default text-3xl md:text-5xl leading-none tracking-tight text-foreground">
                      <span>{w.day}</span>
                      <span className="text-muted-foreground/60 mx-1 md:mx-2">
                        /
                      </span>
                      <span>{w.month}</span>
                    </div>
                    <div className="mt-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-mono" style={{
                  fontVariantCaps: "all-small-caps"
                }}>
                      {w.year}
                    </div>
                  </div>

                  {}
                  <div className="col-span-8 md:col-span-5">
                    <h3 className="font-default text-xl md:text-[28px] leading-snug tracking-tight text-foreground">
                      {w.title}
                    </h3>
                    <p className="mt-2 italic text-sm md:text-base text-muted-foreground font-default">
                      {w.descriptor}
                    </p>
                  </div>

                  {}
                  <div className="col-span-4 md:col-span-2">
                    <div className="text-[11px] md:text-[13px] uppercase tracking-[0.18em] text-muted-foreground font-mono leading-relaxed" style={{
                  fontVariantCaps: "all-small-caps"
                }}>
                      <div>{w.duration}</div>
                      <div>{w.seats}</div>
                    </div>
                  </div>

                  {}
                  <div className="col-span-3 md:col-span-1">
                    <div className="font-default text-lg md:text-[22px] text-foreground">
                      {w.price}
                    </div>
                  </div>

                  {}
                  <div className="col-span-5 md:col-span-2 flex justify-end">
                    {w.status === "sold-out" ? <span className="text-xs md:text-sm uppercase tracking-[0.18em] text-muted-foreground/70 font-mono" style={{
                  fontVariantCaps: "all-small-caps"
                }}>
                        Full
                      </span> : <Button asChild variant="link" className="px-0 h-auto text-foreground hover:text-secondary hover:no-underline font-default text-base md:text-lg">
                        <Link to="/Connect">
                          <span className="relative inline-block">
                            <span className="border-b border-foreground/40 group-hover:border-secondary pb-1 transition-colors">
                              {w.status === "waitlist" ? "Waitlist" : "Register"}
                            </span>
                          </span>
                          <ArrowRight className="ml-2 size-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                      </Button>}
                  </div>
                </div>
              </div>;
        })}
        </div>

        {}
        <div className="mt-16 md:mt-20 flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10">
          <div className="flex items-center gap-6 text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-mono">
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-lg bg-secondary" />
              Available
            </span>
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-lg border border-muted-foreground/60" />
              Waitlist
            </span>
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-lg bg-muted-foreground/40" />
              Full
            </span>
          </div>
          <p className="text-sm italic text-muted-foreground font-default md:ml-auto">
            All programs are provided at no cost to families.
          </p>
        </div>
      </div>
    </section>;
}